import commonUtilities from "utilities/common";
import { CONSTANTS, TOKENS } from "utilities/constants";
import moment from "moment";

/**
 * To get a new place holder key from key and index. for example, "1-concession", to resolve the key conflict in a list
 *
 * @param {Int} index - Index.
 * @param {String} key - A key.
 * @returns {String} A new place holder key.
 */
function getHolderKey(index, key) {
    return index + "-" + key;
}

/**
 * To validate the place holder, based on the data type(for example, NUM) and limitation(INC vs DEC)
 *
 * @param {Array} placeHolderObjects - a list of place holder object.
 * @returns {Boolean} A new place holder key.
 */
function validatePlaceHolders(placeHolderObjects) {

    if((placeHolderObjects || []).length === 0) return true;

    let isValid = true;

    placeHolderObjects.forEach(placeHolderObject => {

        if(placeHolderObject?.data?.tag) {
            const direction = placeHolderObject.data.direction;
            const tag = placeHolderObject.data.tag;
            if(hasMatchTag(tag, [TOKENS.NUM, TOKENS.DATE, TOKENS.MONEY]))
            {
                const value = placeHolderObject?.data?.value;
                const defaultValue = placeHolderObject?.data?.default;
                if(value === undefined || value === "") {
                    isValid = false;
                }
                else{
                    if(!commonUtilities.isLeadingCorrectZero(value ?? "") && !hasMatchTag(tag, [TOKENS.DATE])) {
                        isValid = false;
                    }
                    else {
                        if((direction !== "NONE")){
                            if((hasMatchTag(tag, [TOKENS.NUM, TOKENS.MONEY]))) {
                                const _value = commonUtilities.toFloat(value) || 0;
                                const _defaultValue = commonUtilities.toFloat(defaultValue) || 0;
                                if(direction === TOKENS.INC) {
                                    if(_value < _defaultValue) {
                                        isValid = false;
                                    }
                                }
                                else {
                                    if(_value > _defaultValue) {
                                        isValid = false;
                                    }
                                }
                            }
                            else {
                                let dummyDate = new Date();
                                let _defaultValue = (defaultValue === "") ? dummyDate : moment(defaultValue).toDate();
                                let _value = (value === "") ? dummyDate : moment(value).toDate();
                                if(direction === TOKENS.INC) {
                                    if(_value < _defaultValue) {
                                        isValid = false;
                                    }
                                }
                                else {
                                    if(_value > _defaultValue) {
                                        isValid = false;
                                    }
                                }     
                            }
                        }
                    }
                }
            }
        }
    });
    return isValid;
}

/**
 * To process quote options
 *
 * @param {Object} quote - a quote object.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 * @returns {Array} an array of quoteOption objects.
 */
function processQuoteOptions(quote, forCustomizedQuote) {
    return (quote.quoteOptions || [])
        .map(option =>
            processQuoteOption(option, quote, forCustomizedQuote)
        )
        .filter(Boolean);
}

/**
 * To process a quote option
 *
 * @param {Object} quoteOption - a quoteOption object.
 * @param {Object} quote - a quote object.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 * @returns {Object} a new quoteOption object.
 */
function processQuoteOption(quoteOption, quote, forCustomizedQuote) {
    const productType =
        quoteOption?.productType?.code?.trim().toLowerCase();

    if (productType === CONSTANTS.DEVIATION) {
        return quoteOption;
    }

    let hasSelectedItinerary = false;

    quoteOption.products = quoteOption.products
        ?.map(product => {
            const itineraries = processItineraries(
                product.itineraries,
                productType,
                quote,
                forCustomizedQuote,
                () => (hasSelectedItinerary = true)
            );

            return {
                ...product,
                itineraries
            };
        })
        .filter(product => product.itineraries?.length > 0);

    return hasSelectedItinerary ? quoteOption : null;
}

/**
 * To resolve adult only flag based on product type and markup coverage
 *
 * @param {Object} itinerary - an itinerary object.
 * @param {Object} quote - a quote object.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 * @returns {Boolean} true or false.
 */
function resolveAdultOnly(itinerary, quote, forCustomizedQuote) {
    if (!forCustomizedQuote) return false;

    if (
        quote.markupCoverage === CONSTANTS.MARKUP_COVERAGE_ALL
    ) {
        return false;
    }

    return Boolean(itinerary.adultOnly);
}

/**
 * To process itineraries
 *
 * @param {Array} itineraries - an array of itinerary objects.
 * @param {String} productType - the product type.
 * @param {Object} quote - a quote object.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 * @param {Function} onSelected - callback function when an itinerary is selected.
 * @returns {Array} an array of itinerary objects.
 */
function processItineraries(
    itineraries = [],
    productType,
    quote,
    forCustomizedQuote,
    onSelected
) {
    return itineraries
        .filter(itinerary => !forCustomizedQuote || itinerary.included)
        .map(itinerary => {
            onSelected();

            const markupToApply = resolveMarkup(
                itinerary,
                quote,
                forCustomizedQuote
            );

            const adultOnly = resolveAdultOnly(
                itinerary,
                quote,
                forCustomizedQuote
            );

            applyPricing(
                itinerary,
                productType,
                markupToApply,
                adultOnly,
                forCustomizedQuote
            );

            if (
                forCustomizedQuote &&
                contentHelper.itineraryExpiryDateChanged(itinerary)
            ) {
                itinerary.itineraryValidDate = itinerary.validDate;
            }

            return removeFields(itinerary, [
                "markup",
                "isExpanded",
                "isConcessionExpanded",
                "included",
                "validDate",
                "originalValidDate",
                "showValidDateWarningIcon",
                "adultOnly"
            ]);
        });
}


/**
 * To resolve the markup to apply based on product type and markup coverage
 *
 * @param {Object} itinerary - an itinerary object.
 * @param {Object} quote - a quote object.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 * @returns {Object} A new markup object.
 */
function resolveMarkup(itinerary, quote, forCustomizedQuote) {
    if (!forCustomizedQuote) return {};

    if (
        quote.markupCoverage === CONSTANTS.MARKUP_COVERAGE_ALL
    ) {
        return quote.markup || {};
    }

    return itinerary.markup || {};
}


/**
 * To apply pricing to itinerary based on product type and markup
 *
 * @param {Object} itinerary - an itinerary object.
 * @param {String} productType - the product type.
 * @param {Object} markupToApply - the markup to apply.
 * @param {Boolean} adultOnly - whether adult only or not.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 */
function applyPricing(
    itinerary,
    productType,
    markupToApply,
    adultOnly,
    forCustomizedQuote
) {
    if (
        (productType === CONSTANTS.PACKAGE ||
            productType === CONSTANTS.HOTEL) &&
        itinerary?.itineraryDetail?.roomType
    ) {
        processRoomType(
            itinerary.itineraryDetail.roomType,
            markupToApply,
            adultOnly,
            forCustomizedQuote
        );
    }

    if (
        forCustomizedQuote &&
        productType === CONSTANTS.FLIGHT &&
        itinerary?.itineraryDetail?.flight &&
        itinerary?.itineraryDetail?.taxAndFees
    ) {
        itinerary.itineraryDetail.flight =
            reCalculateFlightPrice(
                itinerary.itineraryDetail.flight,
                itinerary.itineraryDetail.taxAndFees,
                markupToApply
            );
    }
}


/**
 * To remove temporary field from room type object, and re-calculate the pricing based on markup
 *
 * @param {Object} roomType - a room type object.
 * @param {Object} markupToApply - the markup to apply.
 * @param {Boolean} adultOnly - whether adult only or not.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 * @returns {Object} A new room type object.
 */
function processRoomType(
    roomType,
    markupToApply,
    adultOnly,
    forCustomizedQuote
) {
    if (forCustomizedQuote && Array.isArray(roomType.pricing)) {
        roomType.pricing = roomType.pricing.map(pricing =>
            reCalculatePrice(pricing, markupToApply, adultOnly)
        );
    }

    if (forCustomizedQuote && roomType.customConcessions?.length) {
        roomType.concessionDisplayText =
            roomType.customConcessions
                .filter(c => c.included)
                .map(c =>
                    replacePlaceHolderObjects(
                        null,
                        c.concessionDisplayText,
                        c.placeHolderObjects || []
                    )?.join("")
                );
    }

    if (!forCustomizedQuote && roomType.concessionDisplayText?.length) {
        roomType.concessionDisplayText =
            roomType.concessionDisplayText.map(text =>
                replacePlaceHolderObjects(
                    null,
                    text,
                    getPlaceHolderObjects(text) || []
                )?.join("")
            );
    }

    delete roomType.customConcessions;
}

/**
 * To remove temporary fields from promotions array
 *
 * @param {Array} promotions - an array of promotions.
 * @param {String} languageCode - the language code.
 * @param {Boolean} forCustomizedQuote - whether for customized quote or not.
 * @returns {Array} A new promotions array.
 */
function processPromotions(
    promotions = [],
    languageCode,
    forCustomizedQuote
) {
    return promotions
        .filter(p => !forCustomizedQuote || p.included)
        .map((promotion) => {
            const placeholders = forCustomizedQuote
                ? promotion.placeHolderObjects
                : getPlaceHolderObjects(promotion.description);

            const { included, originalText, placeHolderObjects, ...rest } = promotion;
            return {
                ...rest,
                description: replacePlaceHolderObjects(
                    languageCode,
                    rest.description,
                    placeholders || []
                )?.join("")
            };
        });
}

/**
 * To remove temporary fields from terms and conditions array
 *
 * @param {Array} fields - an array of terms and conditions.
 * @returns {Array} A new terms and conditions array.
 */
function processTermsAndConditions(terms = []) {
    return terms.map(({ isExpanded, inEditing, isChanged, isReviewed, originalText, ...rest }) => rest);
}

/**
 * To remove fields from an object
 *
 * @param {Object} obj - an object.
 * @param {Array} fields - an array of field name to of quote response model.
 * @returns {Object} A new object.
 */
function removeFields(obj, fields) {
    return Object.fromEntries(
        Object.entries(obj).filter(
            ([key]) => !fields.includes(key)
        )
    );
}


/**
 * To finalize the quote response
 *
 * @param {Object} quoteResponse - an object of quote response model.
 * @param {Boolean} forCustomizedQuote - an object of quote response model.
 * @returns {Object} A custom quote with new input.
 */
function finalizeQuoteResponse(quoteResponse, forCustomizedQuote = false) {

    if (!quoteResponse?.data) return {};

    let quote = structuredClone(quoteResponse.data);
    const { languageCode } = quote;

    quote.quoteOptions = processQuoteOptions(quote, forCustomizedQuote);
    quote.promotions = processPromotions(
        quote.promotions || [],
        languageCode,
        forCustomizedQuote
    );
    quote.termsAndConditions = processTermsAndConditions(
        quote.termsAndConditions || []
    );

    quote = removeFields(quote, [
        "showItinerariesSelected",
        "totalItineraries",
        "markupCoverage",
        "markup",
        "originalAdditionalRequirements"
    ]);

    return { ...quoteResponse, data: quote };
}

/**
 * To re-calculate the package price based on the markup from UI input
 *
 * @param {Object} price - an object of price from quote response.
 * @param {Object} markup - an object of markup from the input.
 * @param {Boolean} adultOnly - adult only or not. 
 * @returns {Object} A new price object.
 */
function reCalculatePrice(price, markup, adultOnly) {
    let _price = price;
    if(markup && markup.type) {
        if(_price.sellingPrice && _price.netPrice) {
            if(markup.type === "percent") {
                if(_price.ageRange === "") {
                    _price.sellingPrice = commonUtilities.roundNumber(((_price.netPrice ?? 0) * ((commonUtilities.toFloat(commonUtilities.appendZero(markup.adult ?? "0")) + 100) / 100)) + (_price.tax ?? 0) - Math.abs(_price.groupTaxDiscount ?? 0));
                }
                if(markup.child && markup.child !=="0" && _price.ageRange !== "" && _price.ageRange !== "INF") {
                    _price.sellingPrice = commonUtilities.roundNumber(((_price.netPrice ?? 0) * (adultOnly ? 1 : ((commonUtilities.toFloat(commonUtilities.appendZero(markup.child ?? "0")) + 100) / 100))) + (_price.tax ?? 0) - Math.abs(_price.groupTaxDiscount ?? 0));
                }
            }
            else { // type == amount
                if(_price.ageRange === "") {
                    _price.sellingPrice = commonUtilities.roundNumber((_price.netPrice ?? 0) +  commonUtilities.toFloat(commonUtilities.appendZero(markup.adult ?? "0")) + (_price.tax ?? 0) - Math.abs(_price.groupTaxDiscount ?? 0));
                }
                if(_price.ageRange !== "" && _price.ageRange !== "INF") {
                    _price.sellingPrice = commonUtilities.roundNumber((_price.netPrice ?? 0) +  (adultOnly ? 0 : commonUtilities.toFloat(commonUtilities.appendZero(markup.child ?? "0"))) + (_price.tax ?? 0) - Math.abs(_price.groupTaxDiscount ?? 0));
                }
            }
        }
        
    }
    return _price;
}


/**
 * To re-calculate the flight based on the markup from UI input
 *
 * @param {Object} flight - an object of flight from quote response.
 * @param {Object} markup - an object of markup from the input.
 * @returns {Object} A new price object.
 */
function reCalculateFlightPrice(flight, taxAndFees, markup) {
    let _flight = flight;
    if(markup && markup.type && taxAndFees) {
        if(_flight.adultTotalPrice) {
            if(markup.type === "percent") {
                _flight.adultTotalPrice = commonUtilities.roundNumber(((_flight.netAdult ?? 0) * ((commonUtilities.toFloat(commonUtilities.appendZero(markup.adult ?? "0")) + 100) / 100)) + (taxAndFees.perAdult ?? 0));
            }
            else { // type == amount
                _flight.adultTotalPrice = commonUtilities.roundNumber((_flight.netAdult ?? 0) +  commonUtilities.toFloat(commonUtilities.appendZero(markup.adult ?? "0")) + (taxAndFees.perAdult ?? 0));
            }
        }
        if(_flight.childTotalPrice) {
            if(markup.type === "percent") {
                _flight.childTotalPrice = commonUtilities.roundNumber(((_flight.netChild ?? 0) * ((commonUtilities.toFloat(commonUtilities.appendZero(markup.child ?? "0")) + 100) / 100)) + (taxAndFees.perChild ?? 0));
            }
            else { // type == amount
                _flight.childTotalPrice = commonUtilities.roundNumber((_flight.netChild ?? 0) +  commonUtilities.toFloat(commonUtilities.appendZero(markup.child ?? "0")) + (taxAndFees.perChild ?? 0));
            }
        }
    }
    return _flight;
}


/**
 * To proceed place holders in content, replace them with real value
 *
 * @param {String} languageCode - language code.
 * @param {String} content - a piece of content.
 * @param {Object} placeHolderObjects - a list of place holder object collected from UI.
 * @returns {Object} A new price object.
 */
function replacePlaceHolderObjects(languageCode, content, placeHolderObjects) {
    let parts = content.split(/(\{\{.*?\}\})/g);
    let tagIndex = -1;
    return parts.map((part, index) => {
        const match = part.match(/\{\{(.*?)\}\}/);
        if (match) {
            tagIndex = tagIndex + 1;
            const placeHolderObject = placeHolderObjects[tagIndex];
            const holderObj = placeHolderObject?.data || contentHelper.getPlaceHolderObject(match[0]); // match[0] is full holder

            if(hasMatchTag(holderObj.tag, [TOKENS.NUM, TOKENS.DATE, TOKENS.INT, TOKENS.MONEY, TOKENS.SUM, TOKENS.VAR, TOKENS.SUFFIX])) {
                if(tagEqual(holderObj.tag, TOKENS.SUM)) {
                    return getSumValue(placeHolderObject, placeHolderObjects) || "";
                }
                if(tagEqual(holderObj.tag, TOKENS.VAR)) {
                    return "";
                }
                if(tagEqual(holderObj.tag, TOKENS.INT)) {
                    return  holderObj.value || "";
                }
                if(tagEqual(holderObj.tag, TOKENS.SUFFIX)) {
                    return getSuffix(languageCode, placeHolderObject, placeHolderObjects) || "";
                }
                
                if(holderObj.value) {
                    return holderObj.value;
                }
            }
        }
        return part;
    });
}

/**
 * To get all place holder objects from content
 *
 * @param {String} content - a piece of content.
 * @returns {Array} A list of place holder object.
 */
function getPlaceHolderObjects(content) {

    const matches = [...content.matchAll(/\{\{\s*(\w+)\s+(.*?)\s*\}\}/g)];

    const parsed = matches.map((match) => {
        const fullTag = match[0]; // e.g., '{{INT NAME="earnqte" VALUE="1"}}'
        const obj = getPlaceHolderObject(fullTag);

        // Optional default logic (only for INTs: obj.key === "INT" && obj.value !== undefined)
        if (obj.value !== undefined) {
            obj.default = obj.value;
        }

        // if (!obj.name){
        //     obj.name = crypto.randomUUID(); // create a new name if name is not provided
        // }
        return {
            raw: fullTag,   // original tag
            data: obj       // parsed version
        };
    });
    return parsed;
}

/**
 * To convert a place holder tag to an object
 *
 * @param {String} content - a piece of content.
 * @returns {Object} A place holder object.
 */
function getPlaceHolderObject(placeHolder) {

    const match = placeHolder.match(/\{\{\s*(\w+)\s+(.*?)\s*\}\}/);

    if (match) {
        let tag = match[1]; // "INT"
        let direction = null;
        const attributes = match[2]; // 'NAME="earnqte" VALUE="1"'

        const directionMatch = tag.match(/^(.*?)(INC|DEC)?$/);
        if (directionMatch) {
            tag = directionMatch[1];              // Always the date or base part
            if(hasMatchTag(tag, [TOKENS.DATE, TOKENS.MONEY, TOKENS.NUM])) {
                direction = directionMatch[2] || TOKENS.NONE;   // "INC", "DEC", or "NONE"  
            }
        }

        if(tag) {
            const obj = (direction) ? { tag, direction } : { tag };

            attributes.replace(/(\w+)="(.*?)"/g, (_, k, v) => {
                obj[k.toLowerCase()] = v;
                return "";
            });
            return obj;
        }
    }
    return {};
}

/**
 * To get suffix for the place holder object provided
 *
 * @param {String} lang - language, en vs fr.
 * @param {Object} placeHolderObject - a place holder object which is requested to return the suffix
 * @param {Array} placeHolderObjects - a list of place holder object.
 * @returns {String} the suffix for the placeHolderObject.
 */
function getSuffix(lang, placeHolderObject, placeHolderObjects) {
    //SUFFIX
    let suffix = "";
    if(tagEqual(placeHolderObject?.data?.tag, TOKENS.SUFFIX)) {
        let _var = placeHolderObject?.data?.var;

        let obj = getPlaceHolderObjectByName(_var, placeHolderObjects);
        if(obj) {
            let value = (tagEqual(obj?.data?.tag, TOKENS.SUM) ? getSumValue(obj, placeHolderObjects) : getValueByName(_var, placeHolderObjects));
            return getOrdinalSuffix(value, lang);
        }
        
    }
    return suffix;
}

/**
 * To get suffix for number provided, and by language
 *
 * @param {Number} n - a number.
 * @param {String} lang - language code.
 * @returns {String} the suffix.
 */
function getOrdinalSuffix(n, lang) {
    if(lang?.toLowerCase() === "fr") {
        if (n === 1) return "er";
        return "e";
    }
    else {
        const j = n % 10, k = n % 100;
  
        if (j === 1 && k !== 11) return "st";
        if (j === 2 && k !== 12) return "nd";
        if (j === 3 && k !== 13) return "rd";
        return "th";
    }
}

/**
 * To calculate for sum operator
 *
 * @param {Object} placeHolderObject - an object of place holder.
 * @param {Array} placeHolderObjects - a list of place holder object.
 * @returns {any} the result of sum.
 */
function getSumValue(placeHolderObject, placeHolderObjects) {
    
    let value = 0;
    if(tagEqual(placeHolderObject?.data?.tag, TOKENS.SUM)) {
        let { var1, var2 } = placeHolderObject?.data;
        
        let value1 = getValueByName(var1, placeHolderObjects);
        let value2 = getValueByName(var2, placeHolderObjects);

        return  (value1 && value2) ? (value1 + value2) : null;
    }
    return value;
}

/**
 * To get the value by a name
 *
 * @param {String} name - the name of place holder.
 * @param {Array} placeHolderObjects - a list of place holder object.
 * @returns {any} the value of place holder.
 */
function getValueByName(name, placeHolderObjects) {
    const obj = getPlaceHolderObjectByName(name, placeHolderObjects);
    return obj ? getValue(obj) : null;
}

/**
 * To get the object of place holder by a name
 *
 * @param {String} name - the name of place holder.
 * @param {Array} placeHolderObjects - a list of place holder object.
 * @returns {Object} the object of place holder.
 */
function getPlaceHolderObjectByName(name, placeHolderObjects) {
    if(placeHolderObjects?.length > 0) {
        const items = placeHolderObjects.filter(s => s?.data?.name === name);
        if(items && items.length > 0) {
            return items[0];
        }
    }
    return null;
}

/**
 * To get the value from an object of place holder, and convert the value to appropriate type
 *
 * @param {Object} placeHolderObject - the object of place holder.
 * @returns {any} the value of place holder.
 */
function getValue(placeHolderObject){
    if(hasMatchTag(placeHolderObject?.data?.tag, [TOKENS.NUM, TOKENS.MONEY, TOKENS.INT])) {
        if(hasMatchTag(placeHolderObject?.data?.tag, [TOKENS.NUM, TOKENS.INT])) {
            return commonUtilities.toInt(placeHolderObject?.data?.value) || 0;
        }
        else {
            return commonUtilities.toFloat(placeHolderObject?.data?.value) || 0;
        }
    }
    else {
        if(hasMatchTag(placeHolderObject?.data?.tag, [TOKENS.DATE])) {
            return moment(placeHolderObject?.data?.value).toDate();
        }
        else {
            return placeHolderObject?.data?.value;
        }
    }
}

/**
 * To check if the list contains the tag provided
 *
 * @param {String} tag - the tag.
 * @param {Array} list - a list of tag.
 * @returns {Boolean} true vs false.
 */
function hasMatchTag(tag, list) {
    const matches = (list || []).filter(item => (tag || "").toUpperCase() === item.toUpperCase());
    return matches.length !== 0;
}

/**
 * To check if the tag equals the targetTag provided
 *
 * @param {String} tag - the tag.
 * @param {String} targetTag - a tag.
 * @returns {Boolean} true vs false.
 */
function tagEqual(tag, targetTag) {
    return (targetTag || "").toUpperCase() ===  (tag || "").toUpperCase();
}

/**
 * To get a list of place holder(not object)
 *
 * @param {String} content - the content.
 * @returns {Array} a list of place holder.
 */
function getPlaceHolders(content) {

    const matches = [...content.matchAll(/\{\{[^}]+\}\}/g)];
    const results = matches.map(m => m[0]);
    return results;
}

/**
 * To detect if the itinerary expiry date has been changed
 *
 * @param {Object} itinerary - the itinerary.
 * @returns {Boolean} true vs false.
 */
function itineraryExpiryDateChanged(itinerary) {

    if(itinerary && itinerary.validDate && itinerary.originalValidDate) {
        const d1 = new Date(itinerary.validDate);
        const d2 = new Date(itinerary.originalValidDate);

        // Normalize to local midnight
        const date1 = new Date(d1.getFullYear(), d1.getMonth(), d1.getDate());
        const date2   = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());

        return date1.getTime() !== date2.getTime();
    }
    return false;
}


/**
 * To detect if mark filled up correctly
 *
 * @param {Object} markup - the markup.
 * @param {Boolean} adultOnly - true vs false.
 * @returns {Object} { adult: boolean, child: boolean }.
 */
function validateMarkup(markup = {}, adultOnly = false) {
    return {
        adult: !("adult" in markup) || commonUtilities.isFilledValue(markup.adult),
        child: adultOnly || !("child" in markup) || commonUtilities.isFilledValue(markup.child)
    };
}

const contentHelper = {
    getHolderKey,
    finalizeQuoteResponse,
    getPlaceHolders,
    getPlaceHolderObjects,
    getPlaceHolderObject,
    validatePlaceHolders,
    getSumValue,
    hasMatchTag,
    tagEqual,
    getSuffix,
    getPlaceHolderObjectByName,
    itineraryExpiryDateChanged,
    validateMarkup
};

export default contentHelper;
